import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Lock, User } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/Button';

export const Login = () => {
    const { t } = useTranslation();
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [totpCode, setTotpCode] = useState('');
    const [recoveryCode, setRecoveryCode] = useState('');
    const [useRecoveryCode, setUseRecoveryCode] = useState(false);
    const [require2FA, setRequire2FA] = useState(false);
    const [error, setError] = useState('');

    // Trusted Device State
    const [trustDevice, setTrustDevice] = useState(false);
    const [deviceName, setDeviceName] = useState(`Browser on ${navigator.platform}`);

    const { login } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        const checkSetup = async () => {
            try {
                const res = await fetch('/api/auth/status');
                if (res.ok) {
                    const data = await res.json();
                    if (data.setup_required) {
                        navigate('/setup');
                    }
                }
            } catch (err) {
                console.error("Failed to check auth status", err);
            }
        };
        checkSetup();
    }, [navigate]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        try {
            const formData = new FormData();
            formData.append('username', username);
            formData.append('password', password);

            // Send existing device token if available
            const deviceToken = localStorage.getItem('vibe_device_token');
            if (deviceToken) {
                formData.append('device_token', deviceToken);
            }

            if (require2FA) {
                if (useRecoveryCode && recoveryCode) {
                    formData.append('recovery_code', recoveryCode);
                } else if (!useRecoveryCode && totpCode) {
                    formData.append('totp_code', totpCode);
                }

                if (trustDevice) {
                    formData.append('trust_device', 'true');
                    formData.append('device_name', deviceName);
                }
            }

            const res = await fetch('/api/auth/login', {
                method: 'POST',
                body: formData,
            });

            if (res.ok) {
                const data = await res.json();

                // Save new device token if received
                if (data.device_token) {
                    localStorage.setItem('vibe_device_token', data.device_token);
                }

                const userRes = await fetch('/api/auth/me', {
                    headers: { Authorization: `Bearer ${data.access_token}` }
                });
                if (userRes.ok) {
                    const userData = await userRes.json();
                    login(data.access_token, userData);
                    navigate('/');
                }
            } else {
                const errData = await res.json();
                if (errData.detail === '2FA_REQUIRED') {
                    setRequire2FA(true);
                    setError('');
                    return;
                }
                setError(t('login.invalidCredentials'));
            }
        } catch (err) {
            setError(t('login.loginFailed'));
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4">
            <div className="w-full max-w-md p-6 sm:p-8 space-y-6 bg-white rounded-2xl shadow-2xl">
                {/* Logo */}
                <div className="flex flex-col items-center space-y-4">
                    <img
                        src="/logo.jpg"
                        alt="Olga"
                        className="h-28 w-28 sm:h-36 sm:w-36 rounded-full object-cover shadow-lg ring-4 ring-primary/20"
                    />
                    <h1 className="text-3xl sm:text-4xl font-bold tracking-tight bg-gradient-to-r from-primary to-blue-600 bg-clip-text text-transparent">
                        Olga
                    </h1>
                    <div className="text-center">
                        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-gray-900">{t('login.welcomeBack')}</h1>
                        <p className="text-gray-500 text-sm sm:text-base mt-1">
                            {require2FA ? t('login.enter2fa') : t('login.credentials')}
                        </p>
                    </div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                    {!require2FA ? (
                        <>
                            <div className="space-y-2">
                                <label className="text-sm font-medium">{t('login.username')}</label>
                                <div className="relative">
                                    <User className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                                    <input
                                        type="text"
                                        className="w-full pl-9 pr-3 py-2.5 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary focus:outline-none transition-all"
                                        placeholder={t('login.usernamePlaceholder')}
                                        value={username}
                                        onChange={(e) => setUsername(e.target.value)}
                                        required
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <label className="text-sm font-medium">{t('login.password')}</label>
                                <div className="relative">
                                    <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                                    <input
                                        type="password"
                                        className="w-full pl-9 pr-3 py-2.5 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary focus:outline-none transition-all"
                                        placeholder="••••••••"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        required
                                    />
                                </div>
                            </div>
                        </>
                    ) : (
                        <div className="space-y-4 animate-in fade-in slide-in-from-right-4 duration-300">
                            {useRecoveryCode ? (
                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-center block">{t('login.recoveryCode')}</label>
                                    <input
                                        type="text"
                                        className="w-full text-center text-xl tracking-wider font-mono py-3 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary focus:outline-none transition-all"
                                        placeholder={t('login.recoveryCodePlaceholder')}
                                        value={recoveryCode}
                                        onChange={(e) => setRecoveryCode(e.target.value)}
                                        autoFocus
                                        required
                                    />
                                    <p className="text-xs text-center text-muted-foreground">
                                        {t('login.recoveryCodeHint')}
                                    </p>
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-center block">{t('login.twoFactorCode')}</label>
                                    <input
                                        type="text"
                                        className="w-full text-center text-2xl tracking-[0.5em] font-mono py-3 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary focus:outline-none transition-all"
                                        placeholder="000000"
                                        maxLength={6}
                                        value={totpCode}
                                        onChange={(e) => setTotpCode(e.target.value.replace(/[^0-9]/g, ''))}
                                        autoFocus
                                        required
                                    />
                                    <p className="text-xs text-center text-muted-foreground">
                                        {t('login.twoFactorHint')}
                                    </p>
                                </div>
                            )}
                            <div className="flex flex-col space-y-3 pt-2">
                                <div className="flex items-center justify-center space-x-2">
                                    <input
                                        type="checkbox"
                                        id="trust-device"
                                        className="rounded border-gray-300 text-primary focus:ring-primary h-4 w-4"
                                        checked={trustDevice}
                                        onChange={(e) => setTrustDevice(e.target.checked)}
                                    />
                                    <label htmlFor="trust-device" className="text-sm text-muted-foreground select-none cursor-pointer">
                                        {t('login.trustDevice')}
                                    </label>
                                </div>

                                {trustDevice && (
                                    <div className="animate-in fade-in slide-in-from-top-2 duration-200">
                                        <label className="text-xs font-medium text-muted-foreground mb-1 block">{t('login.deviceName')}</label>
                                        <input
                                            type="text"
                                            className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                                            placeholder={t('login.deviceNamePlaceholder')}
                                            value={deviceName}
                                            onChange={(e) => setDeviceName(e.target.value)}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {error && <p className="text-sm text-red-500 text-center">{error}</p>}

                    <div className="space-y-3">
                        <Button className="w-full py-2.5" type="submit">
                            {require2FA ? t('login.verifyAndLogin') : t('login.signIn')}
                        </Button>

                        {require2FA && (
                            <div className="flex flex-col space-y-2 pt-2">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setUseRecoveryCode(!useRecoveryCode);
                                        setTotpCode('');
                                        setRecoveryCode('');
                                        setError('');
                                    }}
                                    className="w-full text-sm text-primary hover:text-primary/80 transition-colors"
                                >
                                    {useRecoveryCode ? t('login.useAuthApp') : t('login.lostDevice')}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => { setRequire2FA(false); setTotpCode(''); setRecoveryCode(''); setError(''); setUseRecoveryCode(false); }}
                                    className="w-full text-sm text-muted-foreground hover:text-primary transition-colors"
                                >
                                    {t('login.backToLogin')}
                                </button>
                            </div>
                        )}
                    </div>
                </form>

                <p className="text-center text-xs text-muted-foreground">
                    {t('login.secureSystem')}
                </p>
            </div>
        </div>
    );
};
